import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play, Pause, Square, SkipForward, Maximize2, GripHorizontal, Check, Volume2, VolumeX } from 'lucide-react';
import useTimerStore from '../../stores/useTimerStore';
import useFlowStore from '../../stores/useFlowStore';
import useAudioStore from '../../stores/useAudioStore';
import useIdentityStore from '../../stores/useIdentityStore';
import useProjectStore from '../../stores/useProjectStore';
import useTaskStore from '../../stores/useTaskStore';
import { stopFreestyleSession } from '../../utils/sessionHelper';
import { PHASE_LABELS, PHASE_COLORS, PHASE_ICONS } from '../../utils/phaseConstants';
import './MiniPlayer.css';

// Always MM:SS
const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export default function MiniPlayer() {
    const navigate = useNavigate();
    const location = useLocation();

    // Freestyle timer
    const timer = useTimerStore();

    // Flow state
    const bpExecState = useFlowStore(s => s.executionState);
    const bpActiveFlow = useFlowStore(s => s.activeFlow);
    const bpStepIndex = useFlowStore(s => s.currentStepIndex);
    const flowTimeRemaining = useFlowStore(s => s.flowTimeRemaining);
    const flowPhase = useFlowStore(s => s.flowPhase);
    const flowIsPaused = useFlowStore(s => s.flowIsPaused);
    const toggleFlowPause = useFlowStore(s => s.toggleFlowPause);
    const stopBpExecution = useFlowStore(s => s.stopExecution);

    const identities = useIdentityStore(s => s.identities);
    const projects = useProjectStore(s => s.projects);
    const allTasks = useTaskStore(s => s.tasks);

    // Determine what's active
    const isFreestyleActive = timer.isRunning;
    const isFlowActive = bpExecState === 'running';
    const isActive = isFreestyleActive || isFlowActive;

    // Don't show mini-player on pages with full timer UI
    const onFullTimerPage = location.pathname === '/focus' || location.pathname === '/flows';
    const show = isActive && !onFullTimerPage;

    // Session-saving stop for freestyle — uses shared utility (single source of truth)
    const handleFreestyleStop = useCallback(() => {
        stopFreestyleSession(timer, projects, allTasks);
    }, [timer, projects, allTasks]);

    // Drag state
    const [position, setPosition] = useState({ x: 20, y: window.innerHeight - 120 });
    const [isDragging, setIsDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const playerRef = useRef(null);

    const handleMouseDown = useCallback((e) => {
        if (e.target.closest('.miniplayer__btn')) return; // Don't drag on buttons
        setIsDragging(true);
        const rect = playerRef.current.getBoundingClientRect();
        dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        e.preventDefault();
    }, []);

    useEffect(() => {
        if (!isDragging) return;

        const handleMove = (e) => {
            const x = Math.max(0, Math.min(window.innerWidth - 320, e.clientX - dragOffset.current.x));
            const y = Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragOffset.current.y));
            setPosition({ x, y });
        };
        const handleUp = () => setIsDragging(false);

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
    }, [isDragging]);

    // Touch drag
    const handleTouchStart = useCallback((e) => {
        if (e.target.closest('.miniplayer__btn')) return;
        setIsDragging(true);
        const touch = e.touches[0];
        const rect = playerRef.current.getBoundingClientRect();
        dragOffset.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }, []);

    useEffect(() => {
        if (!isDragging) return;

        const handleTouchMove = (e) => {
            const touch = e.touches[0];
            const x = Math.max(0, Math.min(window.innerWidth - 320, touch.clientX - dragOffset.current.x));
            const y = Math.max(0, Math.min(window.innerHeight - 80, touch.clientY - dragOffset.current.y));
            setPosition({ x, y });
        };
        const handleTouchEnd = () => setIsDragging(false);

        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd);
        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isDragging]);

    if (!show) return null;

    // ===== Freestyle mini-player =====
    if (isFreestyleActive) {
        const phase = timer.phase;
        const PhaseIcon = PHASE_ICONS[phase];
        const color = PHASE_COLORS[phase];
        const identity = timer.sessionMeta?.identityId
            ? identities.find(i => i.id === timer.sessionMeta.identityId)
            : null;

        const progress = timer.totalTime > 0 ? 1 - timer.timeRemaining / timer.totalTime : 0;
        const circumference = 2 * Math.PI * 18;
        const dashoffset = circumference - progress * circumference;

        const meta = timer.sessionMeta || {};
        const proj = projects.find(p => p.id === meta.projectId);
        const task = allTasks.find(t => t.id === meta.taskId);

        return (
            <div
                ref={playerRef}
                className={`miniplayer miniplayer--freestyle ${isDragging ? 'miniplayer--dragging' : ''}`}
                data-surface="stage"
                style={{ left: position.x, top: position.y, '--phase-color': color }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
            >
                <div className="miniplayer__drag"><GripHorizontal size={12} /></div>

                {/* Ring + time */}
                <div className="miniplayer__timer">
                    <svg viewBox="0 0 44 44" className="miniplayer__ring">
                        <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
                        <circle cx="22" cy="22" r="18" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
                            strokeDasharray={circumference} strokeDashoffset={dashoffset}
                            transform="rotate(-90 22 22)"
                            style={{ filter: `drop-shadow(0 0 4px color-mix(in srgb, ${color} 50%, transparent))` }}
                        />
                    </svg>
                    <PhaseIcon size={14} className="miniplayer__phase-icon" style={{ color }} />
                </div>

                <div className="miniplayer__info">
                    <div className="miniplayer__time">{formatCountdown(timer.timeRemaining)}</div>
                    <div className="miniplayer__label">
                        {identity ? `${identity.emoji} ` : ''}{PHASE_LABELS[phase]}
                    </div>
                    {(proj || task) && (
                        <div className="miniplayer__context">
                            {task ? task.title : proj?.name}
                        </div>
                    )}
                </div>

                {/* Phase dots */}
                <div className="miniplayer__dots">
                    {['prep', 'focus', 'reflect', 'rest'].map((p, i) => {
                        const phases = ['prep', 'focus', 'reflect', 'rest'];
                        const currentIdx = phases.indexOf(phase);
                        return (
                            <span
                                key={p}
                                className={`miniplayer__dot ${i === currentIdx ? 'miniplayer__dot--active' : ''} ${i < currentIdx ? 'miniplayer__dot--done' : ''}`}
                                style={i === currentIdx ? { background: color } : {}}
                            />
                        );
                    })}
                </div>

                <div className="miniplayer__actions">
                    <button className="btn btn-ghost btn-sm btn-icon miniplayer__btn" onClick={() => timer.isPaused ? timer.resumeTimer() : timer.pauseTimer()}>
                        {timer.isPaused ? <Play size={14} /> : <Pause size={14} />}
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon miniplayer__btn" onClick={() => timer.nextPhase()} title="Skip phase">
                        <SkipForward size={14} />
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon miniplayer__btn" onClick={useAudioStore.getState().toggleEnabled} title="Toggle Audio">
                        {useAudioStore.getState().enabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon miniplayer__btn" onClick={handleFreestyleStop}>
                        <Square size={14} />
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon miniplayer__btn" onClick={() => navigate('/focus')} title="Expand">
                        <Maximize2 size={14} />
                    </button>
                </div>
            </div>
        );
    }

    // ===== Flow mini-player =====
    if (isFlowActive && bpActiveFlow) {
        const currentStep = bpActiveFlow.steps[bpStepIndex];
        const phase = flowPhase || 'focus';
        const PhaseIcon = PHASE_ICONS[phase];
        const color = PHASE_COLORS[phase];
        const identity = currentStep?.identityId
            ? identities.find(i => i.id === currentStep.identityId)
            : null;

        const displayTime = flowTimeRemaining != null ? flowTimeRemaining : currentStep?.focusDuration || 0;

        // Progress ring for current step
        const stepDuration = phase === 'focus' ? currentStep?.focusDuration : phase === 'rest' ? currentStep?.restDuration : currentStep?.reflectDuration;
        const progress = stepDuration > 0 ? 1 - displayTime / stepDuration : 0;
        const circumference = 2 * Math.PI * 18;
        const dashoffset = circumference - progress * circumference;

        return (
            <div
                ref={playerRef}
                className={`miniplayer miniplayer--flow ${isDragging ? 'miniplayer--dragging' : ''}`}
                data-surface="stage"
                style={{ left: position.x, top: position.y, '--phase-color': color }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
            >
                <div className="miniplayer__drag"><GripHorizontal size={12} /></div>

                {/* Ring + time */}
                <div className="miniplayer__timer">
                    <svg viewBox="0 0 44 44" className="miniplayer__ring">
                        <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
                        <circle cx="22" cy="22" r="18" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
                            strokeDasharray={circumference} strokeDashoffset={dashoffset}
                            transform="rotate(-90 22 22)"
                            style={{ filter: `drop-shadow(0 0 4px color-mix(in srgb, ${color} 50%, transparent))` }}
                        />
                    </svg>
                    <PhaseIcon size={14} className="miniplayer__phase-icon" style={{ color }} />
                </div>

                <div className="miniplayer__info">
                    <div className="miniplayer__time">{formatCountdown(displayTime)}</div>
                    <div className="miniplayer__label">
                        {identity ? `${identity.emoji} ` : ''}Flow {bpStepIndex + 1}/{bpActiveFlow.steps.length}
                    </div>
                </div>

                {/* Step dots */}
                <div className="miniplayer__dots">
                    {bpActiveFlow.steps.map((_, i) => (
                        <span
                            key={i}
                            className={`miniplayer__dot ${i === bpStepIndex ? 'miniplayer__dot--active' : ''} ${i < bpStepIndex ? 'miniplayer__dot--done' : ''}`}
                            style={i === bpStepIndex ? { background: color } : {}}
                        />
                    ))}
                </div>

                <div className="miniplayer__actions">
                    <button className="btn btn-ghost btn-sm btn-icon miniplayer__btn" onClick={toggleFlowPause} title={flowIsPaused ? 'Resume' : 'Pause'}>
                        {flowIsPaused ? <Play size={14} /> : <Pause size={14} />}
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon miniplayer__btn" onClick={useAudioStore.getState().toggleEnabled} title="Toggle Audio">
                        {useAudioStore.getState().enabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon miniplayer__btn" onClick={stopBpExecution} title="Stop flow">
                        <Square size={14} />
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon miniplayer__btn" onClick={() => navigate('/focus')} title="Expand">
                        <Maximize2 size={14} />
                    </button>
                </div>
            </div>
        );
    }

    return null;
}
